import { useCallback } from "react"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export type CreatePreOrderInput = Partial<PreOrder> & {
  /** Optional override for the generated UUID — useful in tests / drafts. */
  id?: string
}

export type UpdatePreOrderInput = Partial<PreOrder>

export function usePreOrderMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createPreOrder = useCallback(
    async (input: CreatePreOrderInput): Promise<PreOrder> => {
      const id = input.id ?? crypto.randomUUID()
      const now = new Date().toISOString()
      const row: PreOrder = {
        id,
        companyId: input.companyId ?? "",
        userId: input.userId ?? null,
        employeeId: input.employeeId ?? null,
        whatsappChatId: input.whatsappChatId ?? null,
        emailId: input.emailId ?? null,
        smsChatId: input.smsChatId ?? null,
        groupId: input.groupId ?? null,
        sourceType: input.sourceType ?? 3,
        status: input.status ?? 0,
        type: input.type ?? 0,
        category: input.category ?? 0,
        attachedFiles: input.attachedFiles ?? { files: [] },
        invoiceNumber: input.invoiceNumber ?? null,
        paymentDueDate: input.paymentDueDate ?? null,
        paidAt: input.paidAt ?? null,
        isCancelled: input.isCancelled ?? false,
        partnerId: input.partnerId ?? null,
        shippingAddressId: input.shippingAddressId ?? null,
        locationId: input.locationId ?? null,
        routeId: input.routeId ?? null,
        buyerId: input.buyerId ?? null,
        orderId: input.orderId ?? null,
        preOrderNumber: input.preOrderNumber ?? null,
        customerPoNumber: input.customerPoNumber ?? null,
        comment: input.comment ?? null,
        deliveryDate: input.deliveryDate ?? null,
        orderDate: input.orderDate ?? null,
        preOrderMetadata: input.preOrderMetadata ?? null,
        sharedWithUserIds: input.sharedWithUserIds ?? [],
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }

      await storage.put(PRE_ORDER_TABLE, row)
      await changeQueue.enqueue("pre-orders", {
        op: "create",
        id,
        data: row,
      })
      void engine.pushChanges("pre-orders")

      return row
    },
    [storage, changeQueue, engine]
  )

  const updatePreOrder = useCallback(
    async (id: string, input: UpdatePreOrderInput): Promise<void> => {
      const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
      if (!existing) {
        // Surface the miss instead of silently no-oping. Without this throw,
        // the write is lost (no Dexie put, no queue enqueue, no V2 push) but
        // every layer above sees a resolved promise — TanStack mutations
        // mark success and `onSuccess` handlers fire on phantom state. Hits
        // in practice during the initial sync window, after storage clear,
        // or in two-tab races where one tab is mid-resync.
        throw new Error(
          `Cannot update pre-order ${id}: not present in offline store yet`
        )
      }

      const updated: PreOrder = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }
      await storage.put(PRE_ORDER_TABLE, updated)
      await changeQueue.enqueue("pre-orders", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("pre-orders")
    },
    [storage, changeQueue, engine]
  )

  const updatePreOrders = useCallback(
    async (
      items: Array<{ id: string; input: UpdatePreOrderInput }>
    ): Promise<void> => {
      if (items.length === 0) return

      const now = new Date().toISOString()
      const updatedRows: PreOrder[] = []
      const queued: Array<{ id: string; input: UpdatePreOrderInput }> = []

      for (const it of items) {
        const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, it.id)
        if (!existing) continue
        updatedRows.push({ ...existing, ...it.input, updatedAt: now })
        queued.push(it)
      }

      if (updatedRows.length === 0) return

      await storage.putMany(PRE_ORDER_TABLE, updatedRows)
      for (const it of queued) {
        await changeQueue.enqueue("pre-orders", {
          op: "update",
          id: it.id,
          data: it.input,
        })
      }
      void engine.pushChanges("pre-orders")
    },
    [storage, changeQueue, engine]
  )

  const deletePreOrder = useCallback(
    async (id: string): Promise<void> => {
      // Optimistic tombstone (not a hard-delete): the row stays in Dexie with
      // `deletedAt` set so the recap "show deleted" toggle and any open
      // detail view re-render with the deleted styling immediately. The push
      // round-trip + SSE confirmation overwrite this with the server-stamped
      // row. Hard-deleting locally was the root of the "click delete →
      // nothing happens → click again → 404" UX: recap reads from Dexie, so
      // a row that disappeared from storage couldn't be re-styled, the user
      // clicked again, and the server returned 404 because the row was
      // already soft-deleted server-side.
      const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
      if (existing) {
        const now = new Date().toISOString()
        await storage.put(PRE_ORDER_TABLE, {
          ...existing,
          deletedAt: now,
          updatedAt: now,
        })
      }
      await changeQueue.enqueue("pre-orders", { op: "delete", id })
      void engine.pushChanges("pre-orders")
    },
    [storage, changeQueue, engine]
  )

  const undeletePreOrder = useCallback(
    async (id: string): Promise<void> => {
      // Restore a soft-deleted row: clear `deletedAt` locally, send an update
      // op with `deletedAt: null` so the server-side `updateFromSync` wipes
      // the column too. The legacy FastAPI surface had no undelete endpoint;
      // this is offline-first-only and relies on `findByIdForViewer` already
      // returning soft-deleted rows for the viewer-scoped predicate.
      const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
      if (existing) {
        await storage.put(PRE_ORDER_TABLE, {
          ...existing,
          deletedAt: null,
          updatedAt: new Date().toISOString(),
        })
      }
      await changeQueue.enqueue("pre-orders", {
        op: "update",
        id,
        data: { deletedAt: null },
      })
      void engine.pushChanges("pre-orders")
    },
    [storage, changeQueue, engine]
  )

  return {
    createPreOrder,
    updatePreOrder,
    updatePreOrders,
    deletePreOrder,
    undeletePreOrder,
  }
}
